<template>
  <ak-card-header>
    <div v-if="isOwnAccount()" class="pull-right">
      <ak-tooltip :placement="'left'" :has-arrow="false">
        <template #trigger>
          <a
            class="icon-action icon-action--inline icon-action--offset icon-cog color-grey-light"
            @click="changeWidget()"
          ></a>
        </template>
        Change Widget
      </ak-tooltip>
    </div>
    <h4 class="color-grey">Account Details</h4>
  </ak-card-header>
  <ak-card-body :size="'flush'" class="h-full flex flex--column overflow-y">
    <ak-box
      v-if="isLoading"
      class="one-whole h-full flex flex--center flex--justify-center pb-small"
    >
      <ak-loading-spinner />
    </ak-box>
    <ak-box v-if="!isLoading" size="small">
      <div class="flex flex--center pb-tiny">
        <div class="one-third text-bold color-grey px-small">
          Account Status
        </div>
        <div class="two-thirds px-small">
          <ak-tag v-if="account.status !== 'disabled'" ghost :color="'success'">
            Enabled
          </ak-tag>
          <ak-tag v-if="account.status === 'disabled'" ghost :color="'danger'">
            Disabled
          </ak-tag>
        </div>
      </div>
      <div class="flex flex--center pb-tiny">
        <div class="one-third text-bold color-grey px-small">
          Account Manager
        </div>
        <div class="two-thirds px-small">
          {{ accountManagerName || 'None' }}
        </div>
      </div>
      <div class="flex flex--center pb-tiny">
        <div class="one-third text-bold color-grey px-small">Created</div>
        <div class="two-thirds px-small">
          {{ formatDate(account.createdOn) }}
        </div>
      </div>
      <div class="flex flex--center pb-tiny">
        <div class="one-third text-bold color-grey px-small">Edition</div>
        <div class="two-thirds px-small">
          {{
            account.edition === 'business'
              ? 'Standard'
              : account.edition.capitalize()
          }}
        </div>
      </div>
      <div class="flex flex--center pb-tiny">
        <div class="one-third text-bold color-grey px-small">Plan</div>
        <div class="two-thirds px-small">
          {{ account.planType === 'lite' ? 'Free' : 'Paid' }}
        </div>
      </div>
      <div class="flex flex--center pb-tiny">
        <div class="one-third text-bold color-grey px-small">Users</div>
        <div class="two-thirds px-small">
          {{ usersCount }} /
          {{
            account.settings.userLimit !== 0 ? account.settings.userLimit : ''
          }}
          <span :v-if="account.settings.userLimit === 0">Unlimited</span>
        </div>
      </div>
      <div class="flex flex--center pb-tiny">
        <div class="one-third text-bold color-grey px-small">
          Published Apps
        </div>
        <div class="two-thirds px-small">
          {{ account.publishedCount }} /
          {{
            account.plan.appQuota !== 'unlimited' ? account.plan.appQuota : ''
          }}
          <span :v-if="account.plan.appQuota === 'unlimited'">Unlimited</span>
        </div>
      </div>
      <div class="flex flex--center pb-tiny">
        <div class="one-third text-bold color-grey px-small">Active Events</div>
        <div class="two-thirds px-small">
          {{ activeCaptureCount || 0 }}
        </div>
      </div>
      <div class="flex pb-tiny">
        <div class="one-third text-bold color-grey px-small">
          Enabled Features
        </div>
        <div class="two-thirds flex flex--wrap">
          <div v-for="(feature, index) in enabledFeaturesList" :key="index">
            <ak-tag ghost class="mr-tiny mb-tiny">
              {{ feature }}
            </ak-tag>
          </div>
        </div>
      </div>
    </ak-box>
  </ak-card-body>
</template>

<script>
import { computed, ref, onMounted, defineComponent } from 'vue'
import CardHeader from '@akerolabs/akero-vue/src/components/cards/CardHeader.vue'
import CardBody from '@akerolabs/akero-vue/src/components/cards/CardBody.vue'
import Box from '@akerolabs/akero-vue/src/components/box/Box.vue'
import { request } from '@akerolabs/akero-vue/src/utils/request'
import { ToastProgrammatic } from '@akerolabs/akero-vue/src/components/toast'
import { featureList } from '@akerolabs/akero-vue/src/services/features.js'
import { accountObj, agencyObj } from '@akerolabs/akero-vue/src/store'
import date from '@akerolabs/akero-vue/src/utils/date'
import { isOwnAccount } from '@akerolabs/akero-vue/src/services/users'

export default defineComponent({
  name: 'AkWidgetAccountDetails',
  components: {
    [CardHeader.name]: CardHeader,
    [CardBody.name]: CardBody,
    [Box.name]: Box,
  },
  inheritAttrs: false,
  props: {
    /**
     * @type {Function}
     * @description Update the widget state
     */
    changeWidget: {
      type: Function,
      default: () => ({}),
    },
  },
  setup() {
    let n = 10
    const account = accountObj()
    const isLoading = ref(true)

    // Format Created On Date
    const formatDate = (dateString) => {
      return date(dateString).format('DD/MM/YYYY')
    }

    // Get Users for current account and return total number
    const usersCount = ref('')
    const getUsers = async () => {
      let count = new Number()
      const displayError = (ex) => {
        if (ex) console.log(ex)
        ToastProgrammatic.open({
          message: `Error failed to get users`,
          type: 'danger',
        })
      }
      try {
        const { data } = await request.get('/users/', usersRequest.value)
        count = data.data.length
      } catch (ex) {
        count = new Number()
        displayError(ex)
      }
      usersCount.value = count.toString()
    }

    /**
     * @description Returns the request object for making an api request for users
     * @type {Object}
     */
    const usersRequest = computed(() => {
      return {
        params: {
          account: accountObj()._id,
          agency: agencyObj()._id,
          status: JSON.stringify({ $ne: 'disabled' }),
          deleted: JSON.stringify({ $ne: true }),
        },
      }
    })

    // Get accountManager for current account and return name
    const accountManagerName = ref('')
    const getAccountManager = async () => {
      let name = undefined
      const displayError = (ex) => {
        if (ex) console.log(ex)
        ToastProgrammatic.open({
          message: `Error failed to get account manager`,
          type: 'danger',
        })
      }
      try {
        const { data } = await request.get(
          '/users/',
          accountManagerRequest.value,
        )
        name = data.data[0].name
      } catch (ex) {
        name = undefined
        displayError(ex)
      }
      accountManagerName.value = name
    }

    /**
     * @description Returns the request object for making an api request for users
     * @type {Object}
     */
    const accountManagerRequest = computed(() => {
      return {
        params: {
          _id: accountObj().accountManager,
          agency: agencyObj()._id,
        },
      }
    })

    // Get capture events for current account and return total
    const activeCaptureCount = ref('')
    const getCapture = async () => {
      let count = new Number()
      const displayError = (ex) => {
        if (ex) console.log(ex)
        ToastProgrammatic.open({
          message: `Error failed to get events`,
          type: 'danger',
        })
      }
      try {
        const { data } = await request.get('/capture', captureRequest.value)
        count = data.data.length
      } catch (ex) {
        count = new Number()
        displayError(ex)
      }
      activeCaptureCount.value = count.toString()
    }

    /**
     * @description Returns the request object for making an api request for users
     * @type {Object}
     */
    const captureRequest = computed(() => {
      let date = new Date()
      return {
        params: {
          account: accountObj()._id,
          deleted: JSON.stringify({ $ne: true }),
          archived: JSON.stringify({ $ne: true }),
          $or: JSON.stringify([
            {
              $and: [
                { 'endDate.timestamp': { $gt: date.toISOString() } },
                { 'endDate.enable': true },
              ],
            },
            { 'endDate.enable': false },
          ]),
        },
      }
    })

    // Get enabled features
    const enabledFeaturesList = computed(() => featureList(accountObj()))

    /**
     * @description On mount fetch users, account manager and events
     */
    onMounted(async () => {
      isLoading.value = true
      await getUsers()
      await getAccountManager()
      await getCapture()
      isLoading.value = false
    })

    return {
      getUsers,
      formatDate,
      usersCount,
      accountManagerName,
      activeCaptureCount,
      enabledFeaturesList,
      account,
      isOwnAccount,
      isLoading,
      n,
    }
  },
})
</script>
